/**
 * This modal allows a user to select which of their folders the item should be in.
 * The folders it's already in will be selected by default; they can uncheck those folders to remove it.
 * They can also create a new folder in this modal, give it a name (default is 'Untitled Folder'),
 * and place the item in that folder (though the new folder will be saved whether they place the item
 * in that folder or not.)
 */
import PropTypes from 'prop-types'
import {
  any,
  assoc,
  equals,
  find,
  last,
  prepend,
  propEq,
  sort,
  without,
} from 'ramda'
import { Fragment, useEffect, useRef, useState } from 'react'

import {
  Box,
  Button,
  CheckboxControl,
  Dialog,
  HR,
  Heading,
  Icon,
  Image,
  LoadingSpinner,
  SimpleInput,
  Text,
} from '@redriverpress/system'
import FolderOpen from 'Images/folders/folder-open.svg'
import useDidMountEffect from 'Common/hooks/useDidMountEffect'

import folderSorts from '../utils/sort'
import services from '../services'
import { actions, useFoldersContext } from '../context'

/* This component lends several patterns to SaveSelectionDialog.js.
 * Changes to one component may need to be reflected in the other.
 */
export default function MoveItemModal({ isOpen, onClose, item, onMoveItem }) {
  const [{ myFolders }, dispatchFolders] = useFoldersContext()
  const sortedFolders = sort(folderSorts.ascByPosition, myFolders)

  // Initialize `selection` as an object, whose keys are the id of each folder,
  // and whose value is true if that folder contains the item's fileable ID and type already
  const [initialSelection, setInitialSelection] = useState(
    sortedFolders.reduce((fs, f) => ({ ...fs, [f.id]: false }), {})
  )

  // Selection is an object whose keys are folder IDs
  // and values are booleans indicating the check status of that folder
  const [selection, setSelection] = useState(initialSelection)
  const toggleFolderSelection = folderId =>
    setSelection(assoc(folderId, !selection[folderId], selection))

  useEffect(() => {
    if (!isOpen) return

    const fetchInitialSelection = async () => {
      const { data } = await services.getItemFolders({
        itemId: item.fileable.id,
        itemType: item.fileable.type,
      })

      setInitialSelection(prevSelection => ({
        ...prevSelection,
        ...data.foldersIds.reduce((acc, folderId) => {
          acc[folderId] = true
          return acc
        }, {}),
      }))
    }

    fetchInitialSelection()
    setSelection(initialSelection)
  }, [isOpen])

  useEffect(() => {
    // Set the selection back to initialSelection whenever the modal opens/closes.
    // The avoids unsaved changes in the selection persisting across opening/closing.
    setSelection(initialSelection)
  }, [isOpen, initialSelection])

  // Array of IDs of new folders made since opening the modal
  // (Persists across open/close, but not across rerenders)
  const [newFolderIds, setNewFolderIds] = useState([])

  // currentFolders are the sorted folders which existed when
  // the MoveItemModal was last rendered (opening/closing doesn't rerender!)
  const currentFolders = sortedFolders.filter(f => !newFolderIds.includes(f.id))

  // newFolders is sortedFolders without currentFolders
  const newFolders = without(currentFolders, myFolders)

  const newFolderRef = useRef(null)
  const [namingNewFolder, setNamingNewFolder] = useState(false)

  const [isNameError, setIsNameError] = useState(false)

  // When clicking the New Folder button:
  // 1. Show a spinner in the New Folder button
  // 2. Request new folder from BE
  // 3. Put new folder in context
  // 4. Add new folder's ID to newFolderIDs
  // 5. Toggle state for naming new folder
  // 6. Focus the new folder's name input (this step won't work on iOS)
  const handleNewFolder = async () => {
    const { data: newFolder } = await services.createFolder({
      name: 'ellii_temp_default',
    })
    dispatchFolders(actions.createFolder(newFolder))
    setNewFolderIds(current => prepend(newFolder.id, current))
    setSelection({ ...selection, [newFolder.id]: true })
    newFolderRef.current.focus()
  }

  const handleFolderRename = async ({ id, name }) => {
    if (!name) return setIsNameError(true)

    const { data: updatedFolder } = await services.updateFolder({
      id,
      name,
      description: '',
    })
    dispatchFolders(actions.updateFolder(updatedFolder))
    setNamingNewFolder(false)
  }

  const [movePending, setMovePending] = useState(false)
  const handleMove = async () => {
    if (namingNewFolder) return setIsNameError(true)
    setMovePending(true)

    // Filter folder IDs which map to `true` in `selection`
    // to determine which folders the item should now be in
    const inFolders = Object.keys(selection)
      .filter(f => selection[f])
      .map(id => Number(id))

    const { data: changedFolders } = await services.putItem({
      inFolders,
      fileableId: item.fileable.id,
      fileableType: item.fileable.type,
    })

    // Update selection based on the new folder data
    const updatedSelection = changedFolders.reduce((acc, folder) => {
      acc[folder.id] = false
      return acc
    }, {})

    setMovePending(false)
    dispatchFolders(actions.updateChangedFolders(changedFolders))

    setSelection(updatedSelection)
    setInitialSelection(updatedSelection)

    onMoveItem && onMoveItem(!!inFolders.length)
    setIsNameError(false)
    onClose(!!newFolders.length)
  }

  // Don't allow modal to close when any of these conditions are true
  const shouldNotClose = any(x => Boolean(x), [movePending, namingNewFolder])

  const handleClose = () => {
    if (isOpen || shouldNotClose) return

    setSelection(initialSelection)
    setNewFolderIds([])
    setIsNameError(false)
    onClose()
  }

  // If the user reloads the page or cancels
  // the 'new folder' process instead of
  // saving, this method will remove the folder
  // that was created
  const removeDisposedFolders = ids => {
    const tempFolder = myFolders.find(
      folder => folder.name === 'ellii_temp_default'
    )
    if (tempFolder) {
      dispatchFolders(actions.deleteFolder(tempFolder.id))
      setNewFolderIds(
        newFolderIds.filter(folderId => tempFolder.id !== folderId)
      )
      services.deleteFolder(tempFolder.id)
    } else if (ids?.length) {
      ids.forEach(id => {
        dispatchFolders(actions.deleteFolder(id))
        setNewFolderIds(newFolderIds.filter(folderId => id !== folderId))
        services.deleteFolder(id)
      })
    }
  }

  const cancelNewFolderBeforeClose = () => {
    if (newFolders) {
      removeDisposedFolders(newFolders.map(folder => folder.id))
      setNamingNewFolder(false)
      setIsNameError(false)
      onClose(!!newFolders.length)
    }
  }

  // only triggers 'handleNewFolder' if namingFolder is set 'true'
  useEffect(() => {
    if (namingNewFolder) {
      handleNewFolder()
    }
  }, [namingNewFolder])

  useDidMountEffect(() => {
    if (!namingNewFolder) {
      removeDisposedFolders()
    }
  }, [myFolders])

  return (
    <Dialog.Root
      open={shouldNotClose || isOpen}
      onOpenChange={cancelNewFolderBeforeClose}
    >
      <Dialog.Portal>
        <Dialog.Overlay />
        <Dialog.StyledContent
          onPointerDownOutside={e => {
            e.preventDefault()
            if (!newFolders.length) handleClose()
          }}
        >
          <Dialog.StyledTitle>
            <Heading as='h6' mb={0}>
              Save to…
            </Heading>
          </Dialog.StyledTitle>

          {!myFolders.length ? (
            <Box py={5} px={3} textAlign='center' bg='white'>
              <Image
                src={FolderOpen}
                alt='Illustration of an open folder'
                role='presentation'
              />
              <Heading as='h4' mb={0} mt={[3, null, 4]}>
                You have no folders
              </Heading>
            </Box>
          ) : (
            <>
              <Text fontSize={0}>
                Check to add content or uncheck to remove content from
                folder(s).
              </Text>
              {isNameError && (
                <Box
                  display='flex'
                  alignItems='flex-start'
                  p={2}
                  my={[2, null, 3]}
                  bg='red50'
                  borderRadius='small'
                >
                  <Icon
                    icon='exclamation-triangle'
                    weight='solid'
                    color='red700'
                    fontSize={1}
                    pr={2}
                    pt={1}
                  />
                  <Text fontSize={0} color='red700'>
                    Folder’s name can’t be blank.
                  </Text>
                </Box>
              )}
              <Box
                my={[3, null, 4]}
                maxHeight={['12rem', null, '15rem']}
                overflowY='scroll'
                borderRadius='rounded'
                border='2px solid'
                borderColor='gray150'
              >
                {/* List new folders before the rest of the folders */}
                {newFolderIds.map(newId => {
                  const folder = find(propEq('id', newId), newFolders)
                  return (
                    <Fragment key={folder.id}>
                      <Box p={2} display='flex' alignItems='center'>
                        <Box as='label' flexShrink={0}>
                          <CheckboxControl
                            adaptive
                            name='folders'
                            value={folder.id}
                            checked={selection[folder.id]}
                            onChange={() => toggleFolderSelection(folder.id)}
                          />
                        </Box>
                        {/* Render an input if this folder is the most recent new folder
                    and namingNewFolder is true */}
                        {folder.id === newFolderIds[0] && namingNewFolder ? (
                          <SimpleInput
                            ml={2}
                            defaultValue=''
                            placeholder='Type folder name'
                            ref={newFolderRef}
                            autoComplete='off'
                            onFocus={e => e.target.select()}
                            onKeyDown={e =>
                              e.keyCode === 13 &&
                              handleFolderRename({
                                id: folder.id,
                                name: e.target.value,
                              })
                            }
                            onBlur={e =>
                              handleFolderRename({
                                id: folder.id,
                                name: e.target.value,
                              })
                            }
                          />
                        ) : (
                          <Text trunclipse pl={2}>
                            {folder.name}
                          </Text>
                        )}
                      </Box>
                      {/* Don't render an HR under this folder if it's the last one in the newFolders
                    and there are no currentFolders (results in a double bottom border w/ wrapping box) */}
                      {!currentFolders.length &&
                      equals(folder, last(newFolders)) ? null : (
                        <HR borderColor='gray150' />
                      )}
                    </Fragment>
                  )
                })}
                {currentFolders.map(folder => (
                  <Fragment key={folder.id}>
                    <Box p={2} display='flex' alignItems='center'>
                      <Box as='label' flexShrink={0}>
                        <CheckboxControl
                          adaptive
                          name='folders'
                          value={folder.id}
                          checked={selection[folder.id]}
                          onChange={() => toggleFolderSelection(folder.id)}
                        />
                      </Box>
                      <Text trunclipse pl={2}>
                        {folder.name}
                      </Text>
                    </Box>
                    {!equals(folder, last(currentFolders)) && (
                      <HR borderColor='gray150' />
                    )}
                  </Fragment>
                ))}
              </Box>
            </>
          )}

          <Box display={['block', null, 'flex']} justifyContent='space-between'>
            <Button
              adaptive
              secondary
              width={[1, null, 'auto']}
              mb={[3, null, 0]}
              onClick={() => setNamingNewFolder(true)}
              disabled={movePending || namingNewFolder}
            >
              <Text as='span' pl={2}>
                New Folder
              </Text>
            </Button>

            <Box width={[1, null, 'auto']} display='flex'>
              <Button
                adaptive
                secondary
                width={[1 / 2, null, 'auto']}
                mr={2}
                onClick={cancelNewFolderBeforeClose}
              >
                <Icon icon='times' pr={2} />
                Cancel
              </Button>

              <Button
                adaptive
                width={[1 / 2, null, 'auto']}
                onClick={handleMove}
                disabled={
                  equals(selection, initialSelection) ||
                  movePending ||
                  namingNewFolder
                }
              >
                {movePending ? <LoadingSpinner /> : <Icon icon='check' />}
                <Text as='span' pl={2}>
                  Save
                </Text>
              </Button>
            </Box>
          </Box>
        </Dialog.StyledContent>
      </Dialog.Portal>
    </Dialog.Root>
  )
}

MoveItemModal.defaultProps = {
  onMoveItem: null,
}

MoveItemModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  item: PropTypes.shape({
    fileable: PropTypes.shape({
      id: PropTypes.number.isRequired,
      type: PropTypes.string.isRequired,
    }).isRequired,
  }).isRequired,
  onClose: PropTypes.func.isRequired,
  onMoveItem: PropTypes.func,
}
